import { Form, Row } from 'antd';
import { UploadChangeParam } from 'antd/lib/upload';
import { UploadFile } from 'antd/lib/upload/interface';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { ISubj, ISubjPrilozenesoubory } from 'types/amlz';

import ButtonComponent from 'components/atoms/ButtonComponent';
import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import UploadComponent, { IFileList } from 'components/atoms/UploadComponent';
import TranslateText from 'components/hoc/TranslateText';

import { IcisStaty } from 'services/cisStaty';
import fileDownload from 'services/fileDownload';
import { prepareFileAmlz } from 'services/prepareFile';
import { EEsmStav, IOsobyData } from 'services/spravaEmitenta/C4osoby';

import { ETypSouboru } from 'enums/amlz/typSouboru';
import { ETrueFalse } from 'enums/trueFalse';

import jeStatutarSelect from 'data/codebooks/amlz/jeStatutarSelect';

import pscValidation from 'utils/validations/pscValidation';
import rodneCisloValidation from 'utils/validations/rodneCisloValidation';

interface IC4APridatOsobu {
    personData: ISubj | undefined;
    setPersonData: (value: ISubj | undefined) => void;
    setSubmitPersonData: (value: ISubj | null) => void;
    countries?: IcisStaty[];
    subjIndex: number;
    osobyResp?: IOsobyData;
    idZadosti: number | string;
}

const C4APridatOsobu: React.FC<IC4APridatOsobu> = ({
    personData,
    setSubmitPersonData,
    setPersonData,
    countries,
    subjIndex,
    osobyResp,
    idZadosti
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState<boolean>(true);
    const setForceRerender = useState<boolean>(false)[1];
    const [uploading, setUploading] = useState<boolean>();
    const [fileList, setFileList] = useState<IFileList[] | undefined>(
        personData?.jestatutar
            ? personData?.prilozenesoubory?.map((e, i) => {
                  return {
                      uid: String(i),
                      name: e.orignazev ? e.orignazev : 'bez nazvu',
                      fileName: e.tempnazev ? e.tempnazev : 'a',
                      type: e.orignazev ? e.orignazev.split('.').slice(-1)[0] : '',
                      size: 0,
                      status: 'done'
                  };
              })
            : []
    );
    const [prilozeneSoubory, setPrilozeneSoubory] = useState<ISubjPrilozenesoubory[]>(
        personData?.jestatutar ? personData?.prilozenesoubory || [] : []
    );

    const currentOsobaESMdata =
        osobyResp?.osoby &&
        osobyResp.osoby.filter(e => e.jmeno === personData?.jmeno && e.prijmeni === personData.prijmeni)[0];

    const uploadProps = {
        buttonLoading: uploading,
        showDownloadIcon: true,
        onChange: (data: UploadChangeParam) => {
            if (data.file.status === 'uploading' && setUploading) setUploading(true);
            if (data.file.status !== 'uploading' && setUploading) setUploading(false);
            if (data.fileList.length) {
                if (data.file.response) {
                    setPrilozeneSoubory(
                        data.fileList
                            .filter(f => f.status && f.status !== 'error')
                            .map(e => {
                                return {
                                    orignazev: e.name.normalize(),
                                    tempnazev: e.response ? e.response.tempnazev : e.fileName,
                                    oblast: ETypSouboru.SOUBOR_DOKLAD
                                };
                            })
                    );
                    form.validateFields();
                }

                setFileList(
                    data.fileList
                        .filter(f => f.status && f.status !== 'error')
                        .map(e => {
                            return {
                                uid: e.uid,
                                name: e.name.normalize(),
                                fileName: e.fileName ? e.fileName : e.response ? e.response.tempnazev : '',
                                type: e.type!,
                                size: e.size!,
                                status: e.status ? e.status : 'error'
                            };
                        })
                );
            }
        },
        onRemove: (file: UploadFile) => {
            if (fileList?.length) {
                const oldState = [...fileList];
                const newState = oldState.filter(e => e.uid !== file.uid);
                setFileList(newState);
                const newSoubory = [...prilozeneSoubory].filter(e => e.tempnazev !== file.fileName);
                setPrilozeneSoubory(newSoubory);
            }
        },
        onDownload: (file: UploadFile) => {
            setUploading(true);
            prepareFileAmlz(idZadosti, file.fileName ? file.fileName : '')
                .then(resp => {
                    fileDownload(resp.redirect, file.name ? file.name : 'file');
                })
                .finally(() => setUploading(false));
        },
        fileList: fileList,
        buttonDisabled: fileList ? fileList.length > 4 : false
    };

    const handleSelectChange = (name: string, value: string[] | string) => {
        if (typeof value === 'string') {
            form.setFieldsValue({
                [name]: value
            });
        } else {
            form.setFieldsValue({
                [name]: [...value]
            });
        }
        setForceRerender(val => !val);
    };

    useEffect(() => {
        (personData?.jestatutar === true || personData?.jestatutar === false) &&
            form.setFieldsValue({
                jestatutar: String(personData.jestatutar)
            });
        personData &&
            form.setFieldsValue({
                Tstat: personData.stat
            });
        setLoading(false);
        window.scrollTo(0, 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // TODO
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onFinish = (fieldsValue: any) => {
        const values = fieldsValue.Tdatumnar
            ? {
                  ...fieldsValue,
                  Tdatumnar: fieldsValue.Tdatumnar.format('YYYY-MM-DD')
              }
            : {
                  ...fieldsValue
              };

        delete values.soubor;

        const subjData =
            values.jestatutar === ETrueFalse.FALSE
                ? {
                      jestatutar: values.jestatutar === ETrueFalse.TRUE, // premeni value na boolean
                      jmeno: values.Fjmeno,
                      adma: values.Fadma,
                      kost: values.Fkost,
                      mema: values.Fmema,
                      zemepuv: values.Fzemepuv,
                      prijmeni: values.Fprijmeni,
                      pscm: values.Fpscm,
                      stavesm:
                          subjIndex === -1 || personData?.stavesm === EEsmStav.RUCNE ? EEsmStav.RUCNE : EEsmStav.ESMOK,
                      ...(prilozeneSoubory && {
                          prilozenesoubory: prilozeneSoubory
                      })
                  }
                : {
                      jestatutar: values.jestatutar === ETrueFalse.TRUE, // premeni value na boolean
                      adma: values.Tadma,
                      datumnar: values.Tdatumnar,
                      ...(values.Tidmb && {
                          idmb: values.Tidmb
                      }),
                      jmeno: values.Tjmeno,
                      kost: values.Tkost,
                      mema: values.Tmema,
                      misto: values.Tmisto,
                      obcanstvi: values.Tobcanstvi.map((e: string) => {
                          return {
                              stat: e
                          };
                      }),
                      prijmeni: values.Tprijmeni,
                      pscm: values.Tpscm,
                      stat: values.Tstat,
                      stavesm:
                          subjIndex === -1 || personData?.stavesm === EEsmStav.RUCNE ? EEsmStav.RUCNE : EEsmStav.ESMOK,
                      ...(prilozeneSoubory.length && {
                          prilozenesoubory: prilozeneSoubory
                      })
                  };
        setSubmitPersonData(subjData);
    };

    return (
        <Row justify="start">
            {!loading && (
                <Form
                    preserve={false}
                    scrollToFirstError={true}
                    form={form}
                    layout="vertical"
                    initialValues={{
                        remember: true
                    }}
                    onFinish={onFinish}
                    name="C4APridatOsobu"
                    style={{
                        width: '100%'
                    }}
                >
                    <Title level={4} text={'FORMULARE_AMLZ_C4A_TITLE'} />
                    <p>
                        <TranslateText>FORMULARE_AMLZ_C4_OSOBA_TEXT</TranslateText>
                    </p>
                    <SelectComponent
                        name="jestatutar"
                        isFormItem={true}
                        labelParagraph="FORMULARE_AMLZ_C4_OSOBA_MAJITEL"
                        defaultValue={personData?.jestatutar === true || false ? String(personData.jestatutar) : ''}
                        onChange={value => handleSelectChange('jestatutar', value)}
                        data={jeStatutarSelect}
                    />
                    {form.getFieldsValue(['jestatutar']).jestatutar === ETrueFalse.TRUE && (
                        <>
                            <InputComponent
                                label="FORMULARE_JMENA"
                                name="Tjmeno"
                                initialValue={personData?.jmeno}
                                type="text"
                                maxLength={100}
                                disabled={!!currentOsobaESMdata?.jmeno && !!currentOsobaESMdata?.prijmeni}
                            />
                            <InputComponent
                                label="FORMULARE_PRIJMENI"
                                name="Tprijmeni"
                                initialValue={personData?.prijmeni}
                                type="text"
                                maxLength={100}
                                disabled={!!currentOsobaESMdata?.jmeno && !!currentOsobaESMdata?.prijmeni}
                            />
                            <SelectComponent
                                name="Tstat"
                                isFormItem={true}
                                labelParagraph="FORMULARE_STAT_NAROZENI"
                                defaultValue={personData?.stat}
                                onChange={value => handleSelectChange('Tstat', value)}
                                data={countries ? countries : []}
                                disabled={!!currentOsobaESMdata?.stat}
                            />
                            <InputComponent
                                label="FORMULARE_MISTO_NAROZENI"
                                name="Tmisto"
                                initialValue={personData?.misto}
                                type="text"
                                maxLength={50}
                            />
                            {form.getFieldsValue(['Tstat']).Tstat === 'CZ' ? (
                                <InputComponent
                                    label="FORMULARE_RODNE_CISLO_CR"
                                    name="Tidmb"
                                    initialValue={personData?.idmb}
                                    type="text"
                                    maxLength={10}
                                    rules={rodneCisloValidation()}
                                />
                            ) : (
                                <DatePickerComponent
                                    name="Tdatumnar"
                                    label="FORMULARE_DATUM_NAROZENI"
                                    initialValue={
                                        personData?.datumnar
                                            ? dayjs(personData?.datumnar)
                                            : dayjs(currentOsobaESMdata?.datumnar)
                                    }
                                    disabledDate={current => current > dayjs()}
                                    defaultPickerValue={
                                        personData?.datumnar
                                            ? dayjs(personData?.datumnar)
                                            : dayjs(currentOsobaESMdata?.datumnar)
                                    }
                                    defaultValue={
                                        personData?.datumnar
                                            ? dayjs(personData?.datumnar)
                                            : dayjs(currentOsobaESMdata?.datumnar)
                                    }
                                    disabled={!!currentOsobaESMdata?.datumnar}
                                />
                            )}
                            <Title level={5} dividerTitle={true} text="FORMULARE_ADRESA_POBYTU" />
                            <InputComponent
                                label="ULICE_CP"
                                name="Tadma"
                                initialValue={personData?.adma?.trim()}
                                type="text"
                                maxLength={50}
                                disabled={!!currentOsobaESMdata?.adma}
                                {...(currentOsobaESMdata?.adma
                                    ? {
                                          rules: []
                                      }
                                    : {})}
                            />
                            <InputComponent
                                label="MESTO"
                                name="Tmema"
                                initialValue={personData?.mema?.trim()}
                                type="text"
                                maxLength={50}
                                disabled={!!currentOsobaESMdata?.mema}
                                {...(currentOsobaESMdata?.mema
                                    ? {
                                          rules: []
                                      }
                                    : {})}
                            />{' '}
                            <SelectComponent
                                name="Tkost"
                                isFormItem={true}
                                labelParagraph="STAT"
                                defaultValue={personData?.kost?.trim()}
                                onChange={value => handleSelectChange('Tkost', value)}
                                data={countries ? countries : []}
                                disabled={!!currentOsobaESMdata?.kost}
                                {...(currentOsobaESMdata?.kost
                                    ? {
                                          rules: []
                                      }
                                    : {})}
                            />
                            <InputComponent
                                label="PSC"
                                name="Tpscm"
                                initialValue={personData?.pscm?.trim()}
                                type="text"
                                maxLength={10}
                                disabled={!!currentOsobaESMdata?.pscm}
                                rules={currentOsobaESMdata?.pscm ? [] : pscValidation('Tkost')}
                            />
                            <Title
                                level={5}
                                dividerTitle={true}
                                text="FORMULARE_STATNI_OBCANSTVI"
                                style={{
                                    display: 'inline-block'
                                }}
                            />
                            <SelectComponent
                                name="Tobcanstvi"
                                isFormItem={true}
                                labelParagraph="STAT"
                                mode="multiple"
                                defaultValue={
                                    personData?.obcanstvi
                                        ? personData.obcanstvi.map(e => (e.stat ? e.stat : ''))
                                        : undefined
                                }
                                onChange={value => handleSelectChange('obcanstvi', value)}
                                data={countries ? countries : []}
                            />
                            {(subjIndex === -1 || personData?.stavesm === EEsmStav.RUCNE) && (
                                <Form.Item
                                    style={{
                                        marginTop: 16,
                                        marginBottom: 16
                                    }}
                                    name="soubor"
                                    label={<Paragraph text={'VLOZTE_DOKUMENT'} strong={true} />}
                                >
                                    <div
                                        style={{
                                            marginBottom: 22
                                        }}
                                    >
                                        <UploadComponent {...uploadProps} id="C4uploadBtn" />
                                    </div>
                                </Form.Item>
                            )}
                        </>
                    )}
                    {form.getFieldsValue(['jestatutar']).jestatutar === ETrueFalse.FALSE && (
                        <>
                            <InputComponent
                                label="FORMULARE_JMENA"
                                name="Fjmeno"
                                initialValue={personData?.jmeno}
                                type="text"
                                maxLength={100}
                                disabled={!!currentOsobaESMdata?.jmeno && !!currentOsobaESMdata?.prijmeni}
                            />
                            <InputComponent
                                label="FORMULARE_PRIJMENI"
                                name="Fprijmeni"
                                initialValue={personData?.prijmeni}
                                type="text"
                                maxLength={100}
                                disabled={!!currentOsobaESMdata?.jmeno && !!currentOsobaESMdata?.prijmeni}
                            />
                            <Title level={5} dividerTitle={true} text="FORMULARE_ADRESA_POBYTU" />
                            <InputComponent
                                label="ULICE_CP"
                                name="Fadma"
                                maxLength={50}
                                initialValue={personData?.adma?.trim()}
                                type="text"
                                disabled={!!currentOsobaESMdata?.adma}
                                {...(currentOsobaESMdata?.adma
                                    ? {
                                          rules: []
                                      }
                                    : {})}
                            />
                            <InputComponent
                                label="MESTO"
                                name="Fmema"
                                initialValue={personData?.mema?.trim()}
                                type="text"
                                maxLength={50}
                                disabled={!!currentOsobaESMdata?.mema}
                                {...(currentOsobaESMdata?.mema
                                    ? {
                                          rules: []
                                      }
                                    : {})}
                            />{' '}
                            <SelectComponent
                                name="Fkost"
                                isFormItem={true}
                                labelParagraph="STAT"
                                defaultValue={personData?.kost?.trim()}
                                data={countries ? countries : []}
                                disabled={!!currentOsobaESMdata?.kost}
                                {...(currentOsobaESMdata?.kost
                                    ? {
                                          rules: []
                                      }
                                    : {})}
                            />
                            <InputComponent
                                label="PSC"
                                name="Fpscm"
                                initialValue={personData?.pscm?.trim()}
                                type="text"
                                maxLength={10}
                                disabled={!!currentOsobaESMdata?.pscm}
                                rules={currentOsobaESMdata?.pscm ? [] : pscValidation('Fkost')}
                            />
                            <SelectComponent
                                name="Fzemepuv"
                                labelParagraph="FORMULARE_AMLZ_C4_OSOBA_PUVOD"
                                isFormItem={true}
                                mode="multiple"
                                defaultValue={personData?.zemepuv ? personData.zemepuv : undefined}
                                onChange={value => handleSelectChange('Fzemepuv', value)}
                                data={countries ? countries : []}
                            />
                            {(subjIndex === -1 || personData?.stavesm === EEsmStav.RUCNE) && (
                                <Form.Item
                                    style={{
                                        marginTop: 16,
                                        marginBottom: 16
                                    }}
                                    name="soubor"
                                    label={<Paragraph text={'VLOZTE_DOKUMENT'} strong={true} />}
                                    validateTrigger="onChange"
                                >
                                    <div
                                        style={{
                                            marginBottom: 22
                                        }}
                                    >
                                        <UploadComponent {...uploadProps} id="C4uploadBtn" />
                                    </div>
                                </Form.Item>
                            )}
                        </>
                    )}
                    <Row className="button-row">
                        <ButtonComponent
                            buttontext="ZPET"
                            id="C4ZpetBtn"
                            type="default"
                            onClick={() => {
                                setSubmitPersonData(null);
                                setPersonData(undefined);
                            }}
                        />
                        <ButtonComponent
                            buttontext="POKRACOVAT"
                            id="C4PokracovatBtn"
                            type="primary"
                            onClick={() => form.submit()}
                            loading={uploading}
                        />
                    </Row>
                </Form>
            )}
        </Row>
    );
};

export default C4APridatOsobu;
